'use client';

import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

const FAQ = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const faqs = [
    {
      question: "We already have a system. Why switch?",
      answer: "We're not asking you to replace everything overnight. Most clients start with a pilot property or specific pain point. The difference is immediate: you'll have a dedicated coordinator handling the chaos while you focus on growth. Many clients find they actually reduce their overall tool count because Axis consolidates functionality."
    },
    {
      question: "We can't afford another service right now.",
      answer: "Consider this: a full-time facilities manager costs $60-80K/year plus benefits. CoreOps delivers the same (often better) coordination for a fraction of that. Plus, our vendor relationships and bulk scheduling often save clients 10-20% on maintenance costs. It typically pays for itself within the first quarter."
    },
    {
      question: "What types of properties can you manage?",
      answer: "Axis is a universal platform — that's our key differentiator. Apartments, commercial offices, HOA communities, vacation rentals, healthcare facilities, retail centers, industrial parks. The terminology adapts, but the core functionality works for any property type. One client manages 47 properties across 5 different property types, all from one dashboard."
    },
    {
      question: "How fast can we get started?",
      answer: "Most clients are fully onboarded within 2 weeks. We handle the setup, import your property data, and configure the platform for your workflow. If you're choosing Full Service, we also vet and onboard your existing vendors. You'll have full visibility from day one."
    },
    {
      question: "What happens in an emergency?",
      answer: "For Full Service clients: we guarantee 15-minute response time for emergencies, 24/7/365. Our on-call coordinator triages the issue, dispatches the appropriate vendor, and keeps everyone informed. You get notified, but you don't have to drop everything. For SaaS clients, the platform includes emergency escalation workflows you can customize."
    },
    {
      question: "Can property owners see what's happening?",
      answer: "Absolutely — that's a core feature. Owners get their own dashboard with real-time visibility into their properties. No more status update calls or monthly reports that are already outdated. They can see work orders, costs, vendor performance, and more. It's transparency that makes you look professional."
    },
    {
      question: "What if we want to cancel?",
      answer: "No long-term contracts required. We believe in earning your business every month. Most clients sign for 6-12 months after seeing the value, but we don't lock you in. Your data is always exportable, and we'll even help you transition if needed. We've never had to do that, but the option is there."
    },
    {
      question: "Do you replace our existing vendors?",
      answer: "Not unless you want us to. We work with your current vendors — we just manage them better. Track their performance, hold them accountable, and ensure they're responsive. If a vendor isn't meeting standards, we'll help you find alternatives. Over time, clients often consolidate to our vetted network for better pricing and reliability."
    },
  ];

  return (
    <section
      ref={sectionRef}
      id="faq"
      className="relative py-16 sm:py-24 lg:py-32 overflow-hidden"
    >
      <div className="relative max-w-4xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-10 sm:mb-16">
          <span
            className={cn(
              "inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase",
              "bg-primary-500/10 text-primary-400 border border-primary-500/20 mb-4",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
          >
            FAQ
          </span>
          <h2
            className={cn(
              "font-playfair text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-secondary-100 mb-4",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '100ms' }}
          >
            Questions?
            <br />
            <span className="text-primary-400">We Have Answers.</span>
          </h2>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-3 sm:space-y-4">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className={cn(
                "rounded-2xl overflow-hidden",
                "bg-white dark:bg-white/[0.03]",
                "border border-gray-200 dark:border-white/[0.06]",
                openIndex === i && "border-primary-500/20 bg-primary-50 dark:bg-white/[0.04]",
                "transition-all duration-500",
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              )}
              style={{ transitionDelay: `${200 + i * 50}ms` }}
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between gap-3 sm:gap-4 p-4 sm:p-6 text-left"
              >
                <span className={cn(
                  "text-base sm:text-lg font-medium transition-colors",
                  openIndex === i ? "text-primary-600 dark:text-primary-400" : "text-gray-900 dark:text-secondary-100"
                )}>
                  {faq.question}
                </span>
                <ChevronDown
                  className={cn(
                    "w-5 h-5 flex-shrink-0 transition-all duration-300",
                    openIndex === i ? "text-primary-600 dark:text-primary-400 rotate-180" : "text-gray-400 dark:text-secondary-500"
                  )}
                />
              </button>

              <div
                className={cn(
                  "grid transition-all duration-300 ease-out",
                  openIndex === i ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                )}
              >
                <div className="overflow-hidden">
                  <div className="px-4 pb-4 sm:px-6 sm:pb-6 text-sm sm:text-base text-gray-600 dark:text-secondary-400 leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Still have questions */}
        <div
          className={cn(
            "mt-12 text-center",
            "transition-all duration-700",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          )}
          style={{ transitionDelay: '600ms' }}
        >
          <p className="text-gray-500 dark:text-secondary-500">
            Still have questions?{' '}
            <a href="#contact" className="text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 transition-colors">
              Let's talk
            </a>
            {' '}— we're happy to help.
          </p>
        </div>
      </div>
    </section>
  );
};

export { FAQ };
